import type { FC } from "react";

import classNames from "classnames";

import { QuantityInput } from "@jti/core-components/components/QuantityInput";
import { getUser } from "@jti/rest-api";
import { Typography } from "@jti/ui/components/Typography";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import type { PrizeData } from "../../RedeemDialog";
import type { QuantityType } from "../DialogCard";

import styles from "./RedemptionSummary.module.scss";

export type RedemptionSummaryProps = {
  prizeData: PrizeData;
  unitLabel: string;
  pointsLabel: string;
  costLabel: string;
  availablePointsLabel: string;
  balanceAfterRedemptionLabel: string;
  emailLabel?: string;
  emailHint?: string;
  pointsBalance: number;
  quantity: QuantityType;
  quantityAriaLabel?: string;
  increaseAriaLabel?: string;
  decreaseAriaLabel?: string;
};

export const RedemptionSummary: FC<RedemptionSummaryProps> = ({
  prizeData,
  unitLabel,
  pointsLabel,
  costLabel,
  availablePointsLabel,
  balanceAfterRedemptionLabel,
  emailLabel,
  emailHint,
  pointsBalance,
  quantity,
  quantityAriaLabel,
  increaseAriaLabel,
  decreaseAriaLabel,
}) => {
  const totalPrice = prizeData.points * quantity.val;
  const user = getUser();
  const quantityLimit = Math.floor(pointsBalance / prizeData.points);
  const { __ } = useTranslations();

  return (
    <>
      <Typography variant="h3" className={classNames(styles.title, styles.colorGrey3)}>
        {__(prizeData.name)}
      </Typography>
      <div className={classNames(styles.contentRow, styles.quantityInput)}>
        <QuantityInput
          value={quantity.val}
          onChange={quantity.set}
          min={1}
          max={quantityLimit}
          aria-label={quantityAriaLabel}
          increaseButtonAriaLabel={increaseAriaLabel}
          decreaseButtonAriaLabel={decreaseAriaLabel}
        />
        <Typography variant="body2" weight="light" className={styles.colorGrey3}>
          {`${unitLabel} / ${prizeData.points} ${pointsLabel}`}
        </Typography>
      </div>
      <div className={styles.contentRow}>
        <Typography variant="body2" weight="bold" className={styles.colorGrey3}>
          {costLabel}
        </Typography>
        <div className={styles.pointsDisplay}>
          <span className={styles.totalPrice}>{totalPrice}</span>
          <span className={styles.pointsLabel}>{pointsLabel}</span>
        </div>
      </div>
      <div className={styles.contentRow}>
        <Typography variant="body2" weight="bold" className={styles.colorGrey3}>
          {availablePointsLabel}
        </Typography>
        <Typography variant="body2" weight="light" className={styles.colorGrey3}>
          {`${pointsBalance} ${pointsLabel}`}
        </Typography>
      </div>
      <div className={styles.contentRow}>
        <Typography variant="body2" weight="light" className={styles.colorGrey3}>
          {balanceAfterRedemptionLabel}
        </Typography>
        <Typography
          variant="body2"
          weight="light"
          className={styles.colorGrey3}
          dataTestId="redeemDialogBalanceAfterRedemption"
        >
          {`${pointsBalance - totalPrice} ${pointsLabel}`}
        </Typography>
      </div>
      {!!emailLabel && (
        <div className={classNames(styles.contentRow, styles.email)}>
          <Typography variant="body2" weight="light" className={styles.colorGrey3} dataTestId="redeemDialogEmailLabel">
            {emailLabel}
          </Typography>
          <Typography variant="body2" weight="light" className={styles.colorGrey3} dataTestId="redeemDialogEmail">
            {user?.email}
          </Typography>
          {!!emailHint && (
            <Typography
              variant="body2"
              weight="light"
              className={classNames(styles.emailHint, styles.colorGrey4)}
              dataTestId="redeemDialogEmailHint"
            >
              {emailHint}
            </Typography>
          )}
        </div>
      )}
    </>
  );
};
